import { useEffect, useState } from "react";
import {
  Button,
  Table,
  Spin,
  Alert,
  Modal,
  Space,
  message,
} from "antd";
import { ReloadOutlined, FileExcelOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { getBdpoReportCeo, getCeoBlockReport, getCeoGpReport, getBdpoGpReport } from "../api/User";

/* =====================================================
   COMPONENT
===================================================== */
const GramSabhaDistrictReport = ({ dateFilter }) => {
  /* ================= STATE ================= */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* -------- Block Modal -------- */
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockModalTitle, setBlockModalTitle] = useState("");
  const [blockData, setBlockData] = useState([]);
  const [blockLoading, setBlockLoading] = useState(false);

  /* -------- MAIN TABLE PAGINATION -------- */
  const [districtCurrentPage, setDistrictCurrentPage] = useState(1);
  const [districtPageSize, setDistrictPageSize] = useState(10);

  /* -------- BLOCK MODAL PAGINATION -------- */
  const [blockCurrentPage, setBlockCurrentPage] = useState(1);
  const [blockPageSize, setBlockPageSize] = useState(10);

  /* -------- GP MODAL PAGINATION -------- */
  const [gpCurrentPage, setGpCurrentPage] = useState(1);
  const [gpPageSize, setGpPageSize] = useState(10);

  /* -------- GP Modal -------- */
  const [gpModalOpen, setGpModalOpen] = useState(false);
  const [gpModalTitle, setGpModalTitle] = useState("");
  const [gpData, setGpData] = useState([]);
  const [gpLoading, setGpLoading] = useState(false);


  const districtTotals = {
    total_block: data.reduce((s, i) => s + (i.total_block || 0), 0),
    total_panchayat: data.reduce((s, i) => s + (i.total_panchayat || 0), 0),
    total_gram_panchayat_gram_sabha_scheduled: data.reduce((s, i) => s + (i.total_gram_panchayat_gram_sabha_scheduled || 0), 0),
    total_gram_panchayat_gram_sabha_conducted: data.reduce((s, i) => s + (i.total_gram_panchayat_gram_sabha_conducted || 0), 0),
    total_panchayat_voter_updated: data.reduce((s, i) => s + (i.total_panchayat_voter_updated || 0), 0),
    total_panchayat_attendance_updated: data.reduce((s, i) => s + (i.total_panchayat_attendance_updated || 0), 0),
    number_of_panchayat_image_uploaded: data.reduce((s, i) => s + (i.number_of_panchayat_image_uploaded || 0), 0),
    number_of_panchayat_proceeding_book_uploaded: data.reduce((s, i) => s + (i.number_of_panchayat_proceeding_book_uploaded || 0), 0),
    number_of_panchayat_meeting_video_uploaded: data.reduce((s, i) => s + (i.number_of_panchayat_meeting_video_uploaded || 0), 0),
    income_verified_count: data.reduce((s, i) => s + (i.income_verified_count || 0), 0),
    scheme_verified_count: data.reduce((s, i) => s + (i.scheme_verified_count || 0), 0),
    total_panchayat_with_dev_work: data.reduce((s, i) => s + (i.total_panchayat_with_dev_work || 0), 0),
    total_panchayat_with_amrit_sarovar: data.reduce((s, i) => s + (i.total_panchayat_with_amrit_sarovar || 0), 0),
  };

  const blockTotals = {
    total_gp: blockData.reduce((s, i) => s + (i.total_gp || 0), 0),
    gs_scheduled: blockData.reduce((s, i) => s + (i.gs_scheduled || 0), 0),
    gs_conducted: blockData.reduce((s, i) => s + (i.gs_conducted || 0), 0),
    voter_updated: blockData.reduce((s, i) => s + (i.voter_updated || 0), 0),
    attendance_updated: blockData.reduce((s, i) => s + (i.attendance_updated || 0), 0),
    image_uploaded: blockData.reduce((s, i) => s + (i.image_uploaded || 0), 0),
    proceeding_book_uploaded: blockData.reduce((s, i) => s + (i.proceeding_book_uploaded || 0), 0),
    meeting_video_uploaded: blockData.reduce((s, i) => s + (i.meeting_video_uploaded || 0), 0),
    income_verified: blockData.reduce((s, i) => s + (i.income_verified || 0), 0),
    scheme_verified: blockData.reduce((s, i) => s + (i.scheme_verified || 0), 0),
    development_work_uploaded: blockData.reduce((s, i) => s + (i.development_work_uploaded || 0), 0),
    amrit_sarovar_uploaded: blockData.reduce((s, i) => s + (i.amrit_sarovar_uploaded || 0), 0),
  };

  const gpTotals = {
    gs_scheduled: gpData.reduce((s, i) => s + (i.gs_scheduled || 0), 0),
    gs_conducted: gpData.reduce((s, i) => s + (i.gs_conducted || 0), 0),
    voter_updated: gpData.reduce((s, i) => s + (i.voter_updated || 0), 0),
    attendance_updated: gpData.reduce((s, i) => s + (i.attendance_updated || 0), 0),
    image_uploaded: gpData.reduce((s, i) => s + (i.image_uploaded || 0), 0),
    proceeding_book_uploaded: gpData.reduce((s, i) => s + (i.proceeding_book_uploaded || 0), 0),
    meeting_video_uploaded: gpData.reduce((s, i) => s + (i.meeting_video_uploaded || 0), 0),
    income_verified: gpData.reduce((s, i) => s + (i.income_verified || 0), 0),
    scheme_verified: gpData.reduce((s, i) => s + (i.scheme_verified || 0), 0),
    development_work_uploaded: gpData.reduce((s, i) => s + (i.development_work_uploaded || 0), 0),
    amrit_sarovar_uploaded: gpData.reduce((s, i) => s + (i.amrit_sarovar_uploaded || 0), 0),
  };


  // ================= SORT HELPERS =================
  const textSorter = (a, b, key) => {
    const valA = (a[key] || "").toString().toLowerCase();
    const valB = (b[key] || "").toString().toLowerCase();
    return valA.localeCompare(valB);
  };

  const numberSorter = (a, b, key) => {
    const numA = parseFloat(a[key]) || 0;
    const numB = parseFloat(b[key]) || 0;
    return numA - numB;
  };

  /* ================= MAIN TABLE COLUMNS ================= */
  const columns = [
    {
      title: "District Name",
      dataIndex: "district_name",
      width: 160,
      align: "center",
      sorter: (a, b) => textSorter(a, b, "district_name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Block Count",
      dataIndex: "total_block",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_block"),
      sortDirections: ["ascend", "descend"],
      render: (value, record) => (
        <span onClick={() => openBlockModal(record)} style={clickStyle}>
          {value}
        </span>
      ),
    },
    {
      title: "Total Gram Panchayat",
      dataIndex: "total_panchayat",
      width: 160,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_panchayat"),
      sortDirections: ["ascend", "descend"],
      render: (value, record) => (
        <span onClick={() => openGPFromDistrict(record)} style={clickStyle}>
          {value}
        </span>
      ),
    },
    {
      title: "Total Gram Panchayat where Gram Sabha Scheduled",
      dataIndex: "total_gram_panchayat_gram_sabha_scheduled",
      width: 300,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_gram_panchayat_gram_sabha_scheduled"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Total Gram Panchayat where Gram Sabha Conducted",
      dataIndex: "total_gram_panchayat_gram_sabha_conducted",
      width: 300,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_gram_panchayat_gram_sabha_conducted"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "No. of Panchayat where Voter Number Updated",
      dataIndex: "total_panchayat_voter_updated",
      width: 300,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_panchayat_voter_updated"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "No. of Panchayat where Attendance Updated",
      dataIndex: "total_panchayat_attendance_updated",
      width: 300,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_panchayat_attendance_updated"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Number of Panchayat where Image Uploaded",
      dataIndex: "number_of_panchayat_image_uploaded",
      width: 300,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "number_of_panchayat_image_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Number of Panchayat where Proceeding Book Uploaded",
      dataIndex: "number_of_panchayat_proceeding_book_uploaded",
      width: 340,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "number_of_panchayat_proceeding_book_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Number of Panchayat where Meeting Video Uploaded",
      dataIndex: "number_of_panchayat_meeting_video_uploaded",
      width: 340,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "number_of_panchayat_meeting_video_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Income Verified",
      dataIndex: "income_verified_count",
      width: 160,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "income_verified_count"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Scheme Verified",
      dataIndex: "scheme_verified_count",
      width: 160,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "scheme_verified_count"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Number of Panchayat where Development Work Uploaded",
      dataIndex: "total_panchayat_with_dev_work",
      width: 360,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_panchayat_with_dev_work"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Total Panchayat where Detail of Amrit Sarovar Uploaded",
      dataIndex: "total_panchayat_with_amrit_sarovar",
      width: 360,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_panchayat_with_amrit_sarovar"),
      sortDirections: ["ascend", "descend"],
    },
  ];

  /* ================= BLOCK MODAL COLUMNS ================= */
  const blockColumns = [
    {
      title: "District Name",
      dataIndex: "district_name",
      width: 180,
      align: "center",
      fixed: "left",
      sorter: (a, b) => textSorter(a, b, "district_name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Block Name",
      dataIndex: "block_name",
      width: 200,
      align: "center",
      fixed: "left",
      sorter: (a, b) => textSorter(a, b, "block_name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "TOTAL GP",
      dataIndex: "total_gp",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "total_gp"),
      sortDirections: ["ascend", "descend"],
      render: (value, record) => (
        <span
          onClick={() => openGPFromBlock(record)}
          style={clickStyle}
        >
          {value}
        </span>
      ),
    },
    {
      title: "GS Scheduled",
      dataIndex: "gs_scheduled",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "gs_scheduled"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "GS Conducted",
      dataIndex: "gs_conducted",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "gs_conducted"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Voter Updated",
      dataIndex: "voter_updated",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "voter_updated"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Attendance Updated",
      dataIndex: "attendance_updated",
      width: 140,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "attendance_updated"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Image Uploaded",
      dataIndex: "image_uploaded",
      width: 130,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "image_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Proceeding Book",
      dataIndex: "proceeding_book_uploaded",
      width: 150,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "proceeding_book_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Meeting Video",
      dataIndex: "meeting_video_uploaded",
      width: 150,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "meeting_video_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Income Verified",
      dataIndex: "income_verified",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "income_verified"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Scheme Verified",
      dataIndex: "scheme_verified",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "scheme_verified"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Development Work",
      dataIndex: "development_work_uploaded",
      width: 160,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "development_work_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Amrit Sarovar",
      dataIndex: "amrit_sarovar_uploaded",
      width: 150,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "amrit_sarovar_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
  ];

  /* ================= GP MODAL COLUMNS ================= */
  const gpColumns = [
    {
      title: "District Name",
      dataIndex: "district_name",
      width: 180,
      align: "center",
      fixed: "left",
      sorter: (a, b) => textSorter(a, b, "district_name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Block Name",
      dataIndex: "block_name",
      width: 180,
      align: "center",
      fixed: "left",
      sorter: (a, b) => textSorter(a, b, "block_name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Gram Panchayat Name",
      dataIndex: "gp_name",
      width: 240,
      align: "center",
      fixed: "left",
      sorter: (a, b) => textSorter(a, b, "gp_name"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "GS Scheduled",
      dataIndex: "gs_scheduled",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "gs_scheduled"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "GS Conducted",
      dataIndex: "gs_conducted",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "gs_conducted"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Voter Updated",
      dataIndex: "voter_updated",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "voter_updated"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Attendance Updated",
      dataIndex: "attendance_updated",
      width: 140,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "attendance_updated"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Image Uploaded",
      dataIndex: "image_uploaded",
      width: 130,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "image_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Proceeding Book",
      dataIndex: "proceeding_book_uploaded",
      width: 150,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "proceeding_book_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Meeting Video",
      dataIndex: "meeting_video_uploaded",
      width: 150,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "meeting_video_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Income Verified",
      dataIndex: "income_verified",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "income_verified"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Scheme Verified",
      dataIndex: "scheme_verified",
      width: 120,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "scheme_verified"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Development Work",
      dataIndex: "development_work_uploaded",
      width: 160,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "development_work_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Amrit Sarovar",
      dataIndex: "amrit_sarovar_uploaded",
      width: 150,
      align: "center",
      sorter: (a, b) => numberSorter(a, b, "amrit_sarovar_uploaded"),
      sortDirections: ["ascend", "descend"],
    },
  ];

  /* ================= FETCH DISTRICT DATA ================= */
  useEffect(() => {
    fetchDistrictData();
  }, []);

  // Re-fetch when dateFilter changes
  useEffect(() => {
    if (dateFilter !== undefined) {
      fetchDistrictData();
    }
  }, [dateFilter]);

  const fetchDistrictData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        setError("No authentication token found. Please login again.");
        setLoading(false);
        return;
      }

      const result = await getBdpoReportCeo(token, { period: dateFilter });

      if (result.status === 'success') {
        setData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: item.district_name,
          total_block: item.total_block,
          total_panchayat: item.total_panchayat,
          total_gram_panchayat_gram_sabha_scheduled: item.total_gram_panchayat_gram_sabha_scheduled,
          total_gram_panchayat_gram_sabha_conducted: item.total_gram_panchayat_gram_sabha_conducted,
          total_panchayat_voter_updated: item.total_panchayat_voter_updated,
          total_panchayat_attendance_updated: item.total_panchayat_attendance_updated,
          number_of_panchayat_image_uploaded: item.number_of_panchayat_image_uploaded,
          number_of_panchayat_proceeding_book_uploaded: item.number_of_panchayat_proceeding_book_uploaded,
          number_of_panchayat_meeting_video_uploaded: item.number_of_panchayat_meeting_video_uploaded,
          income_verified_count: item.income_verified_count,
          scheme_verified_count: item.scheme_verified_count,
          total_panchayat_with_dev_work: item.total_panchayat_with_dev_work,
          total_panchayat_with_amrit_sarovar: item.total_panchayat_with_amrit_sarovar,
          district_code: item.district_code, // Store district_code for drill-down
        })));
      } else {
        setError(result.message || 'Failed to fetch district data');
      }
    } catch (err) {
      console.error('Error fetching district data:', err);
      setError('Failed to fetch district data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ================= DRILL FUNCTIONS ================= */
  const openBlockModal = async (district) => {
    console.log('Opening block modal for district:', district.district_name, 'District Code:', district.district_code);
    setBlockModalTitle(`Block-wise Report : ${district.district_name}`);
    setBlockModalOpen(true);
    setBlockData([]); // Clear previous data
    setBlockLoading(true); // Start loading

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        console.error('No token found in sessionStorage');
        setError("No authentication token found. Please login again.");
        setBlockLoading(false);
        return;
      }

      console.log('Calling getCeoBlockReport API with district_code:', district.district_code);
      const result = await getCeoBlockReport(token, district.district_code, { period: dateFilter });
      console.log('Block API Response:', result);

      if (result.status === 'success') {
        console.log('Block Data received:', result.data.length, 'records');
        setBlockData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: item.district_name,
          block_name: item.block_name,
          total_gp: item.total_gp,
          gs_scheduled: item.gs_scheduled,
          gs_conducted: item.gs_conducted,
          voter_updated: item.voter_updated,
          attendance_updated: item.attendance_updated,
          image_uploaded: item.image_uploaded,
          proceeding_book_uploaded: item.proceeding_book_uploaded,
          meeting_video_uploaded: item.meeting_video_uploaded,
          income_verified: item.income_verified,
          scheme_verified: item.scheme_verified,
          development_work_uploaded: item.development_work_uploaded,
          amrit_sarovar_uploaded: item.amrit_sarovar_uploaded,
          block_code: item.block_code,
        })));
      } else {
        console.error('Failed to fetch block data:', result.message);
        setError(`Failed to fetch block data: ${result.message}`);
        setBlockData([]);
      }
    } catch (err) {
      console.error('Error fetching block data:', err);
      setError('Failed to fetch block data. Please try again.');
      setBlockData([]);
    } finally {
      setBlockLoading(false); // Stop loading
    }
  };

  const openGPFromDistrict = async (district) => {
    console.log('Opening GP modal for district:', district.district_name, 'District Code:', district.district_code);
    setGpModalTitle(`Gram Panchayat-wise Report : ${district.district_name}`);
    setGpModalOpen(true);
    setGpData([]); // Clear previous data
    setGpLoading(true); // Start loading

    try {
      const token = sessionStorage.getItem("token");

      if (!token) {
        console.error('No token found in sessionStorage');
        setError("No authentication token found. Please login again.");
        setGpLoading(false);
        return;
      }

      console.log('Calling getCeoGpReport API with district_code:', district.district_code);
      const result = await getCeoGpReport(token, district.district_code, { period: dateFilter });
      console.log('GP API Response:', result);

      if (result.status === 'success') {
        console.log('GP Data received:', result.data.length, 'records');
        setGpData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: district.district_name,
          block_name: item.block_name,
          gp_name: item.gp_name || item.panchayat_name,
          gs_scheduled: item.gs_scheduled || 0,
          gs_conducted: item.gs_conducted || 0,
          voter_updated: item.voter_updated || 0,
          attendance_updated: item.attendance_updated || 0,
          image_uploaded: item.image_uploaded || 0,
          proceeding_book_uploaded: item.proceeding_book_uploaded || 0,
          meeting_video_uploaded: item.meeting_video_uploaded || 0,
          income_verified: item.income_verified || 0,
          scheme_verified: item.scheme_verified || 0,
          development_work_uploaded: item.development_work_uploaded || 0,
          amrit_sarovar_uploaded: item.amrit_sarovar_uploaded || 0,
        })));
      } else {
        console.error('Failed to fetch GP data:', result.message);
        setError(`Failed to fetch GP data: ${result.message}`);
        setGpData([]);
      }
    } catch (err) {
      console.error('Error fetching GP data:', err);
      setError('Failed to fetch GP data. Please try again.');
      setGpData([]);
    } finally {
      setGpLoading(false); // Stop loading
    }
  };
  const openGPFromBlock = async (block) => {
    // ✅ Close block modal first
    setBlockModalOpen(false);

    setGpModalTitle(`Gram Panchayat-wise Report : ${block.block_name}`);
    setGpModalOpen(true);
    setGpData([]);
    setGpLoading(true);

    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        setError("No authentication token found. Please login again.");
        setGpLoading(false);
        return;
      }

      const result = await getBdpoGpReport(token, block.block_code, { period: dateFilter });

      if (result.status === "success") {
        setGpData(result.data.map((item, index) => ({
          key: index + 1,
          district_name: block.district_name,
          block_name: block.block_name,
          gp_name: item.gp_name || item.panchayat_name,
          gs_scheduled: item.gs_scheduled || 0,
          gs_conducted: item.gs_conducted || 0,
          voter_updated: item.voter_updated || 0,
          attendance_updated: item.attendance_updated || 0,
          image_uploaded: item.image_uploaded || 0,
          proceeding_book_uploaded: item.proceeding_book_uploaded || 0,
          meeting_video_uploaded: item.meeting_video_uploaded || 0,
          income_verified: item.income_verified || 0,
          scheme_verified: item.scheme_verified || 0,
          development_work_uploaded: item.development_work_uploaded || 0,
          amrit_sarovar_uploaded: item.amrit_sarovar_uploaded || 0,
        })));
      }
    } catch (err) {
      setError("Failed to fetch GP data.");
    } finally {
      setGpLoading(false);
    }
  };

  /* ================= EXPORT FUNCTIONS ================= */

  // Export main district table to Excel
  const handleExportDistrictExcel = () => {
    if (!data || data.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = data.map((row, idx) => ({
        "Sr. No.": idx + 1,
        "District Name": row.district_name || "-",
        "Block Count": row.total_block || 0,
        "Total Gram Panchayat": row.total_panchayat || 0,
        "Total GP where GS Scheduled": row.total_gram_panchayat_gram_sabha_scheduled || 0,
        "Total GP where GS Conducted": row.total_gram_panchayat_gram_sabha_conducted || 0,
        "No. of Panchayat where Voter Updated": row.total_panchayat_voter_updated || 0,
        "No. of Panchayat where Attendance Updated": row.total_panchayat_attendance_updated || 0,
        "No. of Panchayat where Image Uploaded": row.number_of_panchayat_image_uploaded || 0,
        "No. of Panchayat where Proceeding Book Uploaded": row.number_of_panchayat_proceeding_book_uploaded || 0,
        "No. of Panchayat where Meeting Video Uploaded": row.number_of_panchayat_meeting_video_uploaded || 0,
        "Income Verified Count": row.income_verified_count || 0,
        "Scheme Verified Count": row.scheme_verified_count || 0,
        "Total Panchayat with Development Work": row.total_panchayat_with_dev_work || 0,
        "Total Panchayat with Amrit Sarovar": row.total_panchayat_with_amrit_sarovar || 0,
      }));
      excelData.push({
        "Sr. No.": "",
        "District Name": "GRAND TOTAL",
        "Block Count": districtTotals.total_block,
        "Total Gram Panchayat": districtTotals.total_panchayat,
        "Total GP where GS Scheduled": districtTotals.total_gram_panchayat_gram_sabha_scheduled,
        "Total GP where GS Conducted": districtTotals.total_gram_panchayat_gram_sabha_conducted,
        "No. of Panchayat where Voter Updated": districtTotals.total_panchayat_voter_updated,
        "No. of Panchayat where Attendance Updated": districtTotals.total_panchayat_attendance_updated,
        "No. of Panchayat where Image Uploaded": districtTotals.number_of_panchayat_image_uploaded,
        "No. of Panchayat where Proceeding Book Uploaded": districtTotals.number_of_panchayat_proceeding_book_uploaded,
        "No. of Panchayat where Meeting Video Uploaded": districtTotals.number_of_panchayat_meeting_video_uploaded,
        "Income Verified Count": districtTotals.income_verified_count,
        "Scheme Verified Count": districtTotals.scheme_verified_count,
        "Total Panchayat with Development Work": districtTotals.total_panchayat_with_dev_work,
        "Total Panchayat with Amrit Sarovar": districtTotals.total_panchayat_with_amrit_sarovar,
      });


      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "District Report");

      const fileName = `CEO_District_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };

  // Export block modal data to Excel
  const handleExportBlockExcel = () => {
    if (!blockData || blockData.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = blockData.map((row, idx) => ({
        "Sr. No.": idx + 1,
        "District Name": row.district_name || "-",
        "Block Name": row.block_name || "-",
        "Total GP": row.total_gp || 0,
        "GS Scheduled": row.gs_scheduled || 0,
        "GS Conducted": row.gs_conducted || 0,
        "Voter Updated": row.voter_updated || 0,
        "Attendance Updated": row.attendance_updated || 0,
        "Image Uploaded": row.image_uploaded || 0,
        "Proceeding Book Uploaded": row.proceeding_book_uploaded || 0,
        "Meeting Video Uploaded": row.meeting_video_uploaded || 0,
        "Income Verified": row.income_verified || 0,
        "Scheme Verified": row.scheme_verified || 0,
        "Development Work Uploaded": row.development_work_uploaded || 0,
        "Amrit Sarovar Uploaded": row.amrit_sarovar_uploaded || 0,
      }));
      excelData.push({
        "Sr. No.": "",
        "District Name": "GRAND TOTAL",
        "Block Name": "",
        "Total GP": blockTotals.total_gp,
        "GS Scheduled": blockTotals.gs_scheduled,
        "GS Conducted": blockTotals.gs_conducted,
        "Voter Updated": blockTotals.voter_updated,
        "Attendance Updated": blockTotals.attendance_updated,
        "Image Uploaded": blockTotals.image_uploaded,
        "Proceeding Book Uploaded": blockTotals.proceeding_book_uploaded,
        "Meeting Video Uploaded": blockTotals.meeting_video_uploaded,
        "Income Verified": blockTotals.income_verified,
        "Scheme Verified": blockTotals.scheme_verified,
        "Development Work Uploaded": blockTotals.development_work_uploaded,
        "Amrit Sarovar Uploaded": blockTotals.amrit_sarovar_uploaded,
      });


      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Block Report");

      const fileName = `CEO_Block_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };

  // Export GP modal data to Excel
  const handleExportGpExcel = () => {
    if (!gpData || gpData.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      const excelData = gpData.map((row, idx) => ({
        "Sr. No.": idx + 1,
        "District Name": row.district_name || "-",
        "Block Name": row.block_name || "-",
        "GP Name": row.gp_name || "-",
        "GS Scheduled": row.gs_scheduled || 0,
        "GS Conducted": row.gs_conducted || 0,
        "Voter Updated": row.voter_updated || 0,
        "Attendance Updated": row.attendance_updated || 0,
        "Image Uploaded": row.image_uploaded || 0,
        "Proceeding Book Uploaded": row.proceeding_book_uploaded || 0,
        "Meeting Video Uploaded": row.meeting_video_uploaded || 0,
        "Income Verified": row.income_verified || 0,
        "Scheme Verified": row.scheme_verified || 0,
        "Development Work Uploaded": row.development_work_uploaded || 0,
        "Amrit Sarovar Uploaded": row.amrit_sarovar_uploaded || 0,
      }));
      excelData.push({
        "Sr. No.": "",
        "District Name": "GRAND TOTAL",
        "Block Name": "",
        "GP Name": "",
        "GS Scheduled": gpTotals.gs_scheduled,
        "GS Conducted": gpTotals.gs_conducted,
        "Voter Updated": gpTotals.voter_updated,
        "Attendance Updated": gpTotals.attendance_updated,
        "Image Uploaded": gpTotals.image_uploaded,
        "Proceeding Book Uploaded": gpTotals.proceeding_book_uploaded,
        "Meeting Video Uploaded": gpTotals.meeting_video_uploaded,
        "Income Verified": gpTotals.income_verified,
        "Scheme Verified": gpTotals.scheme_verified,
        "Development Work Uploaded": gpTotals.development_work_uploaded,
        "Amrit Sarovar Uploaded": gpTotals.amrit_sarovar_uploaded,
      });


      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "GP Report");

      const fileName = `CEO_GP_Report_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success("Excel file exported successfully");
    } catch (err) {
      console.error("Export Excel error:", err);
      message.error("Failed to export Excel file");
    }
  };

  /* ================= UI ================= */
  return (
    <div className="report-container">
      <div className="report-card">
        <div className="report-header">
          <div>
            <h2 className="report-title">Detailed report of Special Gram Sabha (CEO)</h2>

          </div>

          <Space>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExportDistrictExcel}
              disabled={loading || data.length === 0}
            >
              Export Excel
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchDistrictData}>
              Refresh
            </Button>
          </Space>
        </div>

        {error && <Alert type="error" message={error} />}

        {loading ? (
          <Spin />
        ) : (
          <Table
            columns={columns}
            dataSource={data}
            rowKey="key"
            bordered
            size="small"
            scroll={{ x: 3000, y: 420 }}
            pagination={{
              current: districtCurrentPage,
              pageSize: districtPageSize,
              total: data.length,
              showSizeChanger: true,
              // showQuickJumper: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={(pagination) => {
              setDistrictCurrentPage(pagination.current);
              setDistrictPageSize(pagination.pageSize);
            }}
          />
        )}
        {data.length > 0 && (
          <div style={totalBoxStyle}>
            <strong>Grand Totals:</strong>
            <span>Total Blocks: <strong>{districtTotals.total_block}</strong></span>
            <span>Total GP: <strong>{districtTotals.total_panchayat}</strong></span>
            <span>GS Scheduled: <strong>{districtTotals.total_gram_panchayat_gram_sabha_scheduled}</strong></span>
            <span>GS Conducted: <strong>{districtTotals.total_gram_panchayat_gram_sabha_conducted}</strong></span>
            <span>Voter Updated: <strong>{districtTotals.total_panchayat_voter_updated}</strong></span>
            <span>Attendance Updated: <strong>{districtTotals.total_panchayat_attendance_updated}</strong></span>
            <span>Images Uploaded: <strong>{districtTotals.number_of_panchayat_image_uploaded}</strong></span>
            <span>Proceeding Books: <strong>{districtTotals.number_of_panchayat_proceeding_book_uploaded}</strong></span>
            <span>Meeting Videos: <strong>{districtTotals.number_of_panchayat_meeting_video_uploaded}</strong></span>
            <span>Income Verified: <strong>{districtTotals.income_verified_count}</strong></span>
            <span>Scheme Verified: <strong>{districtTotals.scheme_verified_count}</strong></span>
            <span>Dev Work Uploaded: <strong>{districtTotals.total_panchayat_with_dev_work}</strong></span>
            <span>Amrit Sarovar: <strong>{districtTotals.total_panchayat_with_amrit_sarovar}</strong></span>
          </div>
        )}

      </div>

      {/* ========== BLOCK MODAL ========== */}
      <Modal
        open={blockModalOpen}
        onCancel={() => setBlockModalOpen(false)}
        width={1200}
        footer={null}
        destroyOnClose
        style={{ top: 20 }}
        bodyStyle={{ padding: '16px 0 0 0' }}
        title={
          <div style={{ paddingRight: 40 }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                {blockModalTitle}
              </span>

              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExportBlockExcel}
                disabled={blockLoading || blockData.length === 0}
              >
                Export Excel
              </Button>
            </div>
          </div>
        }
      >

        {blockLoading ? (

          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={blockColumns}
            dataSource={blockData}
            rowKey="key"
            bordered
            size="small"
            scroll={{ x: 1800, y: 400 }}
            pagination={{
              current: blockCurrentPage,
              pageSize: blockPageSize,
              total: blockData.length,
              showSizeChanger: true,
              // showQuickJumper: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={(pagination) => {
              setBlockCurrentPage(pagination.current);
              setBlockPageSize(pagination.pageSize);
            }}
          />

        )}
        {blockData.length > 0 && (
          <div style={totalBoxStyle}>
            <strong>Grand Totals:</strong>
            <span>Total GP: <strong>{blockTotals.total_gp}</strong></span>
            <span>GS Scheduled: <strong>{blockTotals.gs_scheduled}</strong></span>
            <span>GS Conducted: <strong>{blockTotals.gs_conducted}</strong></span>
            <span>Voter Updated: <strong>{blockTotals.voter_updated}</strong></span>
            <span>Attendance Updated: <strong>{blockTotals.attendance_updated}</strong></span>
            <span>Images Uploaded: <strong>{blockTotals.image_uploaded}</strong></span>
            <span>Proceeding Books: <strong>{blockTotals.proceeding_book_uploaded}</strong></span>
            <span>Meeting Videos: <strong>{blockTotals.meeting_video_uploaded}</strong></span>
            <span>Income Verified: <strong>{blockTotals.income_verified}</strong></span>
            <span>Scheme Verified: <strong>{blockTotals.scheme_verified}</strong></span>
            <span>Dev Work Uploaded: <strong>{blockTotals.development_work_uploaded}</strong></span>
            <span>Amrit Sarovar: <strong>{blockTotals.amrit_sarovar_uploaded}</strong></span>
          </div>
        )}


      </Modal>

      {/* ========== GP MODAL ========== */}
      <Modal
        open={gpModalOpen}
        onCancel={() => setGpModalOpen(false)}
        width={1200}
        footer={null}
        destroyOnClose
        style={{ top: 20 }}
        bodyStyle={{ padding: '16px 0 0 0' }}
        title={
          <div style={{ paddingRight: 40 }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                {gpModalTitle}
              </span>

              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExportGpExcel}
                disabled={gpLoading || gpData.length === 0}
              >
                Export Excel
              </Button>
            </div>
          </div>
        }
      >

        {gpLoading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            columns={gpColumns}
            dataSource={gpData}
            rowKey="key"
            bordered
            size="small"
            scroll={{ x: 1600, y: 400 }}
            pagination={{
              current: gpCurrentPage,
              pageSize: gpPageSize,
              total: gpData.length,
              showSizeChanger: true,
              // showQuickJumper: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} records`,
            }}
            onChange={(pagination) => {
              setGpCurrentPage(pagination.current);
              setGpPageSize(pagination.pageSize);
            }}
          />
        )}
        {gpData.length > 0 && (
          <div style={totalBoxStyle}>
            <strong>Grand Totals:</strong>
            <span>GS Scheduled: <strong>{gpTotals.gs_scheduled}</strong></span>
            <span>GS Conducted: <strong>{gpTotals.gs_conducted}</strong></span>
            <span>Voter Updated: <strong>{gpTotals.voter_updated}</strong></span>
            <span>Attendance Updated: <strong>{gpTotals.attendance_updated}</strong></span>
            <span>Images Uploaded: <strong>{gpTotals.image_uploaded}</strong></span>
            <span>Proceeding Books: <strong>{gpTotals.proceeding_book_uploaded}</strong></span>
            <span>Meeting Videos: <strong>{gpTotals.meeting_video_uploaded}</strong></span>
            <span>Income Verified: <strong>{gpTotals.income_verified}</strong></span>
            <span>Scheme Verified: <strong>{gpTotals.scheme_verified}</strong></span>
            <span>Dev Work Uploaded: <strong>{gpTotals.development_work_uploaded}</strong></span>
            <span>Amrit Sarovar: <strong>{gpTotals.amrit_sarovar_uploaded}</strong></span>
          </div>
        )}

      </Modal>
    </div>
  );
};

/* ================= CLICK STYLE ================= */
const clickStyle = {
  color: "#1677ff",
  cursor: "pointer",
  textDecoration: "underline",
  fontWeight: 500,
};
/* ================= TOTAL BOX STYLE ================= */
const totalBoxStyle = {
  backgroundColor: "#e6f7ff",
  border: "1px solid #91d5ff",
  borderRadius: "6px",
  padding: "10px 14px",
  marginTop: "12px",
  display: "flex",
  flexWrap: "wrap",
  gap: "14px",
  fontSize: "12px",
};

export default GramSabhaDistrictReport;